/**
 * Överenskommelse om avstående från besittningsskydd – Lokal (Formulär R-15).
 *
 * Hyresnämndens blankett HN3: a lokalhyresgäst's indirekt besittningsskydd
 * (12 kap. 57–60 §§ jordabalken) waived for a stated cause; the form is also
 * the application to hyresnämnden. Page 2 is the official explanatory text
 * with its own § 1–5, numbered in a scope of their own (the first point
 * restarts the numbering).
 *
 * Legal text verbatim from the official HN3 PDF (Domstolsverket, 2023-07).
 * DO NOT paraphrase or reorder.
 */
import { em, evaluateCondition, group, h, list, nodes, option, optionList, p, pageBreak, section, strong, t, val } from '@hyranai/regta-document-ir';
import { dateVal, fieldRows, partySlot, pick, propertySlot, selectSlot, shown, slotText, textVal, ticked, whenOption } from '../common/index.js';
import { hnPartyRows, infoSection, ruledCaption } from '../avstaende_besittningsskydd_bostad/blocks.js';
import { answer, flag, legalMeta, legalSigningBlock } from '../meddelande_18e/kit.js';
const LEGAL = legalMeta('Hyresnämndens blankett HN3 (Domstolsverket, 2023-07)');
const COTENANT = ' (medhyresgäst)';
const KEY = 'avstaende_besittningsskydd_lokal';
export const LEASE_COPY_OPTIONS = [
    { code: 'ja', value: 'Ja', label: 'Ja', summary: 'A copy of the lease agreement is attached to the application.' },
    { code: 'nej', value: 'Nej', label: 'Nej', summary: 'No copy of the lease agreement is attached.' },
];
export const VALIDITY_OPTIONS = [
    {
        code: 'fem_ar',
        value: 'Fem år från tillträdesdagen',
        label: 'Fem år från tillträdesdagen',
        summary: 'Waiver applies if the tenancy ends within five years of the access date.',
    },
    {
        code: 'langre_tid',
        value: 'Längre tid (särskilda skäl)',
        label: 'Längre tid – vi åberopar särskilda skäl',
        summary: 'A longer validity period is requested, citing special grounds.',
    },
];
const LONGER = VALIDITY_OPTIONS[1];
export const rubrik = {
    ...LEGAL,
    blockKey: `${KEY}.rubrik`,
    version: 'v1',
    title: 'Rubrik',
    summary: 'The approval-not-always-needed warning and the form’s title, “Ansökan till hyresnämnd (HN3)”.',
    slots: [],
    compile: () => [
        p([strong('OBS! Nämndens godkännande behövs inte alltid. Läs informationen om avstående från besittningsskydd nedan innan ni fyller i ansökan.')], { variant: 'caption' }),
        h(1, 'Överenskommelse om avstående från besittningsskydd – Lokal', { align: 'center', keepWithNext: true }),
        p('Ansökan till hyresnämnd (HN3)', { align: 'center', variant: 'caption' }),
    ],
};
const contactRows = (input, prefix) => [
    ['E-post', [textVal(input, `${prefix}_email`)]],
    ['Person-/Org.nr', [textVal(input, `${prefix}_org_number`)]],
    ['Telefon dagtid', [textVal(input, `${prefix}_phone_day`)]],
];
const partySlots = (prefix, role, ordinal, suffix, required) => [
    partySlot(`${prefix}_name`, `Namn${suffix}`, role, ordinal, 'name', { required }),
    partySlot(`${prefix}_address`, `Gatuadress${suffix}`, role, ordinal, 'street', { type: 'textarea', required }),
    partySlot(`${prefix}_postal_code`, `Postnummer${suffix}`, role, ordinal, 'postalCode', { required }),
    partySlot(`${prefix}_city`, `Ort${suffix}`, role, ordinal, 'city', { required }),
    partySlot(`${prefix}_email`, `E-post${suffix}`, role, ordinal, 'email'),
    partySlot(`${prefix}_org_number`, `Personnr / Org.nr${suffix}`, role, ordinal, 'legalId'),
    partySlot(`${prefix}_phone_day`, `Telefon dagtid${suffix}`, role, ordinal, 'phone'),
];
export const hyresvard = {
    ...LEGAL,
    blockKey: `${KEY}.hyresvard`,
    version: 'v1',
    title: 'Hyresvärd / Uthyrare',
    summary: 'The landlord’s name, address, id number and contact details.',
    slots: partySlots('landlord', 'lessor', 0, '', true),
    compile: (input) => [
        section('hyresvard-uthyrare', 'Hyresvärd / Uthyrare', hnPartyRows(input, 'landlord', contactRows(input, 'landlord'))),
    ],
};
/** A co-tenant is present when any of its printed fields is filled. */
const COTENANT_PRESENT = {
    any: ['cotenant_name', 'cotenant_address', 'cotenant_phone_day', 'cotenant_org_number'].map((key) => ({ key, isSet: true })),
};
const showCotenant = (input, ctx) => shown(ctx, evaluateCondition(COTENANT_PRESENT, input));
export const hyresgaster = {
    ...LEGAL,
    blockKey: `${KEY}.hyresgaster`,
    version: 'v1',
    title: 'Hyresgäst (-er)',
    summary: 'The tenant, and a co-tenant when one is given.',
    slots: [...partySlots('tenant', 'lessee', 0, '', true), ...partySlots('cotenant', 'lessee', 1, COTENANT, false)],
    compile: (input, ctx) => [
        section('hyresgast-er', 'Hyresgäst (-er)', nodes(...hnPartyRows(input, 'tenant', contactRows(input, 'tenant')), showCotenant(input, ctx) ? p([strong('Medhyresgäst')], { keepWithNext: true }) : null, ...(showCotenant(input, ctx) ? hnPartyRows(input, 'cotenant', contactRows(input, 'cotenant')) : [])), { sideBySide: true }),
    ],
};
export const lokalen = {
    ...LEGAL,
    blockKey: `${KEY}.lokalen`,
    version: 'v1',
    title: 'Lokalen',
    summary: 'Street, town and fastighetsbeteckning of the let premises.',
    slots: [
        propertySlot('premises_street', 'Gata och nr', 'address', { required: true }),
        propertySlot('premises_city', 'Ort', 'municipality', { required: true }),
        propertySlot('property_designation', 'Fastighetsbeteckning', 'designation', { required: true }),
    ],
    compile: (input) => [
        section('lokalen', 'Lokalen', [
            ...nodes(fieldRows([
                ['Gata och nr', [textVal(input, 'premises_street')]],
                ['Ort', [textVal(input, 'premises_city')]],
                ['Fastighetsbeteckning', [textVal(input, 'property_designation')]],
            ])),
        ]),
    ],
};
export const hyresavtalet = {
    ...LEGAL,
    blockKey: `${KEY}.hyresavtalet`,
    version: 'v1',
    title: 'Hyresavtalet',
    summary: 'Signing and access dates of the lease, and whether a copy is attached.',
    slots: [
        {
            key: 'lease_signed_date',
            label: 'Dag då avtalet undertecknades',
            type: 'date',
            required: true,
            help: 'Datum då det lokalhyresavtal som överenskommelsen avser undertecknades.',
        },
        {
            key: 'lease_access_date',
            label: 'Tillträdesdag',
            type: 'date',
            required: true,
            help: 'Tillträdesdag enligt hyresavtalet. Giltighetstiden räknas från denna dag (se informationen om avstående från besittningsskydd).',
        },
        selectSlot('lease_copy_attached', 'Avtalskopia bifogas', LEASE_COPY_OPTIONS, {
            default: 'ja',
            help: 'Kopia på hyresavtalet bör ges in till hyresnämnden.',
        }),
    ],
    compile: (input, ctx) => {
        const copy = pick(LEASE_COPY_OPTIONS, input.lease_copy_attached);
        return [
            section('hyresavtalet', 'Hyresavtalet', [
                ...nodes(fieldRows([
                    ['Dag då avtalet undertecknades', [dateVal(ctx, input, 'lease_signed_date')]],
                    ['Tillträdesdag', [dateVal(ctx, input, 'lease_access_date')]],
                ])),
                p('Avtalskopia bifogas (bör ges in):', { keepWithNext: true }),
                optionList(LEASE_COPY_OPTIONS.map((o) => option(ticked(ctx, copy?.code === o.code), o.label, { code: o.code })), { sourceKey: 'lease_copy_attached', select: 'single', layout: 'inline' }),
            ]),
        ];
    },
};
export const innehall = {
    ...LEGAL,
    blockKey: `${KEY}.overenskommelsens-innehall`,
    version: 'v1',
    title: 'Överenskommelsens innehåll',
    summary: '57–60 §§ JB do not apply on termination for demolition/major rebuild or another stated cause; valid five years or longer for special grounds.',
    slots: [
        {
            key: 'reason_rivning_ombyggnad',
            label: 'Hyresvärden säger upp avtalet på grund av att lokalen ligger i ett hus som ska rivas eller undergå större ombyggnad',
            type: 'boolean',
            help: 'Kryssa i om hyresvärden förbehåller sig rätten att säga upp avtalet på grund av rivning eller större ombyggnad av huset.',
        },
        {
            key: 'reason_other',
            label: 'Hyresvärden säger upp avtalet på grund av en annan orsak (anges tydligt nedan)',
            type: 'boolean',
            help: 'För att hyresnämnden ska godkänna överenskommelsen måste en godtagbar orsak anges. Säger hyresvärden senare upp avtalet av en annan orsak än den som anges här, gäller vanliga regler för besittningsskyddet.',
        },
        {
            key: 'reason_other_description',
            label: 'Beskrivning av annan orsak',
            type: 'textarea',
            visibleWhen: { key: 'reason_other', equals: true },
            help: 'Ange tydligt den orsak som ska gälla. Vagt formulerade skäl godkänns inte av hyresnämnden.',
        },
        selectSlot('validity_period', 'Giltighetstid – överenskommelsen gäller om hyresförhållandet upphör inom', VALIDITY_OPTIONS, {
            required: true,
            default: 'fem_ar',
            help: 'Normalt godkänns endast överenskommelser för hyresförhållanden som upphör inom fem år från tillträdesdagen. Längre tid måste motiveras särskilt.',
        }),
        {
            key: 'validity_extended_years',
            label: 'Antal år från tillträdesdagen',
            type: 'number',
            visibleWhen: whenOption('validity_period', LONGER),
            help: 'Den längre giltighetstid (antal år) som parterna begär.',
        },
        {
            key: 'validity_extended_motivation',
            label: 'Särskilda skäl för längre giltighetstid än fem år',
            type: 'textarea',
            visibleWhen: whenOption('validity_period', LONGER),
            help: 'Motivera varför överenskommelsen ska gälla längre tid än fem år.',
        },
    ],
    compile: (input, ctx) => {
        const longer = pick(VALIDITY_OPTIONS, input.validity_period)?.code === LONGER.code;
        const years = longer && typeof input.validity_extended_years === 'number' ? String(input.validity_extended_years) : longer ? slotText(input.validity_extended_years) : null;
        return [
            section('overenskommelsens-innehall', 'Överenskommelsens innehåll', nodes(p('Vi kommer överens om att bestämmelserna i 12 kap. 57–60 §§ jordabalken inte gäller i följande fall.'), optionList([
                option(ticked(ctx, flag(input, 'reason_rivning_ombyggnad')), 'Hyresvärden säger upp avtalet på grund av att lokalen ligger i ett hus som ska rivas eller undergå större ombyggnad', { code: 'reason_rivning_ombyggnad', sourceKey: 'reason_rivning_ombyggnad' }),
                option(ticked(ctx, flag(input, 'reason_other')), 'Hyresvärden säger upp avtalet på grund av en annan orsak som anges tydligt, nämligen:', { code: 'reason_other', sourceKey: 'reason_other' }),
            ], { select: 'multi' }), shown(ctx, flag(input, 'reason_other')) ? answer(input, 'reason_other_description') : null, p('Överenskommelsen gäller om hyresförhållandet upphör inom', { keepWithNext: true }), optionList([
                option(!longer, [strong('fem år'), t(' från tillträdesdagen.')], { code: 'fem_ar' }),
                option(ticked(ctx, longer), [
                    val(years, { sourceKey: 'validity_extended_years', blankWidth: 'short' }),
                    t(' '),
                    strong('år'),
                    t(' från tillträdesdagen. Vi åberopar särskilda skäl som grund för längre giltighetstid än fem år:'),
                ], { code: 'langre_tid' }),
            ], { select: 'single' }), shown(ctx, longer) ? answer(input, 'validity_extended_motivation') : null)),
        ];
    },
};
export const ansokan = {
    ...LEGAL,
    blockKey: `${KEY}.ansokan`,
    version: 'v1',
    title: 'Ansökan',
    summary: 'The parties apply for hyresnämnden’s approval.',
    slots: [],
    compile: () => [section('ansokan', 'Ansökan', [p('Vi ansöker härmed om att hyresnämnden godkänner vår överenskommelse.')])],
};
export const underskrift = legalSigningBlock(LEGAL, {
    blockKey: `${KEY}.underskrift`,
    title: 'Underskrifter',
    summary: 'Signing statement and panels for landlord, tenant and an eventual co-tenant.',
    defaults: {
        ref: 'underskrift',
        title: 'Underskrifter',
        statement: true,
        parties: [
            { label: 'Hyresvärd / Uthyrare', role: 'lessor', ordinal: 0 },
            { label: 'Hyresgäst', role: 'lessee', ordinal: 0 },
            { label: 'Eventuell medhyresgäst', role: 'lessee', ordinal: 1 },
        ],
    },
});
export const hyresnamndensBeslut = {
    ...LEGAL,
    blockKey: `${KEY}.hyresnamndens-beslut`,
    version: 'v1',
    title: 'Hyresnämndens beslut',
    summary: 'The box hyresnämnden fills in: approval, date and signature.',
    slots: [],
    compile: () => [
        section('hyresnamndens-beslut', 'Hyresnämndens beslut', [
            p([em('Fylls i av hyresnämnden.')], { variant: 'fine_print' }),
            group([
                optionList([option(false, 'Överenskommelsen godkänns')]),
                p([em('Observera: Hyresnämndens godkännande innebär inte att nämnden har godkänt villkoren i hyresavtalet.')]),
                ruledCaption('Datum'),
                ruledCaption('Hyresnämndens underskrift'),
            ], { variant: 'boxed' }),
        ]),
    ],
};
export const information = {
    ...LEGAL,
    blockKey: `${KEY}.information`,
    version: 'v1',
    title: 'Information om avstående från besittningsskydd – Lokal (HN3)',
    summary: 'The HN3 page-2 explanation: indirekt besittningsskydd, when it can be waived, when approval is (not) needed.',
    slots: [],
    compile: () => [
        pageBreak(),
        h(1, 'Information om avstående från besittningsskydd – Lokal (HN3)', { align: 'center', keepWithNext: true }),
        infoSection('besittningsskydd-vad-innebar-det', 'Besittningsskydd, vad innebär det?', [
            p('En lokalhyresgäst har i regel indirekt besittningsskydd enligt bestämmelserna i 12 kap 57–60 §§ jordabalken, om inte hyresförhållandet upphör innan det har varat längre än nio månader i följd. Besittningsskyddet innebär att hyresgästen i vissa fall kan få ersättning av hyresvärden för ekonomisk förlust på grund av att hyresförhållandet upphör.'),
            p('I besittningsskyddet ligger också att hyresgästen har rätt till skäligt uppskov med avflyttningen.'),
        ], true),
        infoSection('besittningsskyddet-kan-avtalas-bort', 'Besittningsskyddet kan avtalas bort i vissa fall', [
            p('Hyresvärden och hyresgästen kan i vissa fall komma överens om att hyresgästen avstår från besittningsskyddet. Överenskommelse ska alltid göras i en särskilt upprättad och undertecknad handling. I följande avsnitt beskrivs vad som ytterligare krävs för att överenskommelsen ska vara giltig.'),
        ]),
        infoSection('nar-behovs-godkannande', 'När behövs hyresnämndens godkännande?', [
            p('Huvudregeln är att en överenskommelse om avstående från besittningsskydd behöver godkännas av hyresnämnden.'),
            p('Vid överenskommelse som ska godkännas bör hyresvärden och hyresgästen använda sig av blankett HN3 (denna blankett).'),
            p('För att hyresnämnden ska kunna godkänna överenskommelsen måste en godtagbar orsak till kommande uppsägning anges, dvs. varför besittningsskyddet ska brytas när hyresavtalet sägs upp. Om hyresvärden säger upp avtalet av något annat skäl än det som angetts i överenskommelsen, gäller vanliga regler för besittningsskyddet.'),
            p('Normalt godkänns endast överenskommelser för sådana hyresförhållanden som upphör inom fem år från tillträdesdagen. Blanketten är därför förtryckt med denna tid. Om längre tid begärs, måste detta motiveras särskilt.'),
        ]),
        infoSection('nar-behovs-inte-godkannande', 'När behövs inte nämndens godkännande?', [
            p([strong('Alternativ 1')], { keepWithNext: true }),
            p('Ifall en överenskommelse om avstående från besittningsskydd träffas för en tid på högst fem år från det att hyresförhållandet inleds gäller den utan hyresnämndens godkännande om den avser'),
            list([
                'avstående p.g.a. att hyresvärden ska bedriva egen verksamhet i lokalen, eller',
                'andrahandsupplåtelse och hyresavtalet mellan fastighetsägaren och dennes hyresgäst ska upphöra.',
            ], { ordered: true }),
            p('I dessa fall ska en överenskommelse utformas i enlighet med regeringens formulär 4–5 enligt förordningen SFS 2005:1148. Hyresnämndens blankett HN5 kan användas.'),
            p([strong('Alternativ 2')], { keepWithNext: true }),
            p('Ifall en överenskommelse om avstående från besittningsskydd träffas efter mer än nio månaders hyresförhållande behövs inget godkännande av hyresnämnden.'),
            p('Något särskilt formulär behövs inte i dessa fall, men överenskommelsen ska alltid träffas i en särskilt upprättad handling.'),
        ]),
        infoSection('mer-information', 'Mer information', [
            p('På hyresnämndernas webbplats finns mer information om hyresrättsliga frågor: domstol.se/hyra-arrende'),
        ]),
    ],
};
export const AVSTAENDE_BESITTNINGSSKYDD_LOKAL_BLOCKS = [
    rubrik,
    hyresvard,
    hyresgaster,
    lokalen,
    hyresavtalet,
    innehall,
    ansokan,
    underskrift,
    hyresnamndensBeslut,
    information,
];
//# sourceMappingURL=blocks.js.map